// One committed voice utterance, opaque (BUILD 2 C2-8, `voice-no-keyword-routing`
// rule 2). Its text is held here and leaves only through `toChatText()`, which
// the turn body (./turn-body.ts) and the composer insert (./composer-insert.ts)
// call; everything else, the controller and the guards included, reads only
// the structural fields: the session, the turn, what committed it, the
// provider's language, a word count and when its capture began.
import type { Utterance, VoiceCommit } from './contracts';

export type UtteranceInit = {
  sessionId: string;
  turn: number;
  commit: VoiceCommit;
  /** The provider's `committed.lang` (structural). */
  lang?: string;
  /** The provider's word count for the turn, when it sent one (an endpoint's `words`). */
  words?: number;
  /** When the turn's capture began, on `performance.now()`. */
  startedAt: number;
  text: string;
};

const texts = new WeakMap<Utterance, string>();

/**
 * Words in a text by `Intl.Segmenter`'s word boundaries (a count, never a
 * reading of the words); whitespace runs where the segmenter is missing.
 */
export function countWords(text: string): number {
  const Segmenter = (Intl as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (!Segmenter) return text.split(' ').filter((part) => part.trim() !== '').length;
  let count = 0;
  for (const segment of new Segmenter(undefined, { granularity: 'word' }).segment(text))
    if (segment.isWordLike) count += 1;
  return count;
}

export function createUtterance(init: UtteranceInit): Utterance {
  const text = init.text.trim();
  const utterance: Utterance = Object.freeze({
    sessionId: init.sessionId,
    turn: init.turn,
    commit: init.commit,
    ...(init.lang ? { lang: init.lang } : {}),
    words: init.words ?? countWords(text),
    startedAt: init.startedAt,
    toChatText: () => text,
  });
  texts.set(utterance, text);
  return utterance;
}

/**
 * Speech that resumed inside the undo window joins the pending turn (C2-8):
 * the texts in order, the later commit, language and session, the earlier start.
 */
export function mergeUtterances(first: Utterance, next: Utterance): Utterance {
  const joined = [texts.get(first) ?? '', texts.get(next) ?? ''].filter(Boolean).join(' ');
  return createUtterance({
    sessionId: next.sessionId,
    turn: next.turn,
    commit: next.commit,
    lang: next.lang ?? first.lang,
    words: first.words + next.words,
    startedAt: first.startedAt,
    text: joined,
  });
}

/** Whether an utterance carries any text (a structural check on its length). */
export const isEmptyUtterance = (utterance: Utterance): boolean =>
  (texts.get(utterance) ?? '').length === 0;
